import { Typography, Slider } from '@mui/material';
import { useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Button } from '@design-system/components/button/button';
import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sprinkles } from '@design-system/sprinkles.css';
import { wrapper } from './SelectThumbnail.css';
import { VideoTimeLine } from './VideoTimeLine';
import { VideoView } from '../view-models/editVideo.viewModel';

type VideoThumbnailSelect = {
  video: VideoView;
  onChange: (timeInSeconds: number) => void;
  onRestore: () => void;
  submitting: boolean;
};

const SHORT_VIDEO_BREAKPOINT = 90;

export function SelectThumbnail(p: VideoThumbnailSelect) {
  const playerRef = useRef<ReactPlayer>(null);
  const [duration, setDuration] = useState(p.video.duration);
  const [sliderValue, setSliderValue] = useState(0);

  const handleSliderChange = (
    _event: Event | null,
    newValue: number | number[],
  ) => {
    const value = newValue as number;
    setSliderValue(value);
    if (playerRef?.current) {
      playerRef.current.seekTo(value);
    }
  };

  const saveThumbnail = () => {
    p.onChange(sliderValue);
  };
  const isMuxVideo = p.video.isMux;
  const thumbnailUrl = p.video.thumbnailUrl;
  const ratio = p.video.width > p.video.height ? 16 / 9 : 9 / 16;

  return (
    <div className={wrapper}>
      {isMuxVideo && p.video?.duration > SHORT_VIDEO_BREAKPOINT ? (
        <>
          <img
            src={`${thumbnailUrl}?smart_crop=true&time=${sliderValue}`}
            className="max-w-full max-h-[372px] object-contain"
          />
        </>
      ) : (
        <div className="flex justify-center bg-black">
          <ReactPlayer
            onDuration={(d) => !duration && setDuration(d)}
            ref={playerRef}
            playing={false}
            seconds={sliderValue}
            playsinline
            height={300}
            url={p.video.url}
            style={{
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          />
        </div>
      )}
      <div className="mt-5">
        <Typography variant="h5" className="font-semibold">
          Use slider to select the frame for the thumbnail.
        </Typography>
      </div>
      <div className="w-full relative" style={{ height: 80 }}>
        <VideoTimeLine
          duration={duration}
          height={80}
          width={770}
          src={p.video.downloadUrl || p.video.url}
          ratio={ratio}
        />
        <Slider
          value={sliderValue}
          max={duration}
          step={0.01}
          onChange={handleSliderChange}
          sx={{
            position: 'absolute',
            top: '25px',
            '&.MuiSlider-root > .MuiSlider-rail': {
              display: 'none',
            },
            '&.MuiSlider-root > .MuiSlider-track': {
              display: 'none',
            },
            '&.MuiSlider-root > .MuiSlider-thumb': {
              width: '4px',
              height: '80px',
              color: 'red',
              borderRadius: '1px',
              boxShadow: 'none',
            },
          }}
        />
      </div>
      <div
        className={sprinkles({
          display: 'flex',
          alignItems: 'center',
          gap: 'm',
          justifyContent: 'end',
        })}
      >
        <Button
          variant="text"
          color="neutral"
          onClick={p.onRestore}
          disabled={p.submitting}
        >
          <div
            className={sprinkles({
              display: 'flex',
              alignItems: 'center',
              gap: 'm',
            })}
          >
            <FontAwesomeIcon icon={faArrowRotateLeft} /> Restore changes
          </div>
        </Button>
        <Button
          onClick={saveThumbnail}
          color="primaryTonal"
          disabled={p.submitting}
        >
          Select thumbnail
        </Button>
      </div>
    </div>
  );
}
